import type { SerializedError } from "@reduxjs/toolkit"
import { type FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query"
import { useParams } from "react-router"

import { useProjectsGetByNameQuery } from "@/api/routes/projects"
import { usePublicProjectsGetByNameQuery } from "@/api/routes/public"
import { SubscriptionStatus } from "@/billing/billing"
import { useSessionUser } from "@/hooks/use-session-user"
import type { Project } from "@/types"

export type ProjectHook = {
  username: string
  projectName: string
  data?: Project
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  error?: FetchBaseQueryError | SerializedError
  refetch: () => void
  isPublic: boolean
}

// Smarter project hook that avoids unnecessary authenticated requests
export function useProjectSmart(): ProjectHook {
  const { username, projectName } = useParams()
  const { isLoggedIn, isLoading: isAuthLoading } = useSessionUser()

  if (!username) {
    throw new Error(
      "useProjectSmart hook used on a route without a username param",
    )
  }
  if (!projectName) {
    throw new Error(
      "useProjectSmart hook used on a route without a projectName param",
    )
  }

  // Skip authenticated request if not logged in
  const authenticatedReq = useProjectsGetByNameQuery(
    isLoggedIn ? { username, project_name: projectName } : skipToken,
  )

  // Try public request if not logged in or if authenticated request failed with 401
  const shouldTryPublic =
    !isLoggedIn ||
    (authenticatedReq.isError &&
      authenticatedReq.error &&
      "status" in authenticatedReq.error &&
      authenticatedReq.error.status === 401)

  const publicReq = usePublicProjectsGetByNameQuery(
    shouldTryPublic ? { username, project_name: projectName } : skipToken,
  )

  // Determine which request to use
  const activeReq =
    isLoggedIn && !shouldTryPublic ? authenticatedReq : publicReq

  // Convert public project data to minimal Project shape if needed
  const projectData: Project | undefined = (() => {
    if (!activeReq.data) return undefined

    if (isLoggedIn && !shouldTryPublic) {
      // Full project data from authenticated endpoint
      return authenticatedReq.data
    }
    // Convert PublicProjectInfo to minimal Project shape
    const publicData = publicReq.data
    return {
      ...publicData,
      username,
      owner_id: "", // Not available in public data
      created_at: 0, // Not available in public data
      updated_at: 0, // Not available in public data
      billing_name: "", // Not available in public data
      billing_email: "", // Not available in public data
      entitlements: [], // Not available in public data
      subscription_status: SubscriptionStatus.unknown,
    } as Project
  })()

  return {
    username,
    projectName,
    data: projectData,
    isLoading: isAuthLoading || activeReq.isLoading,
    isSuccess: activeReq.isSuccess,
    isError: activeReq.isError,
    error: activeReq.error,
    refetch: activeReq.refetch,
    isPublic: !isLoggedIn || shouldTryPublic,
  }
}
